import gql from 'graphql-tag';

import { Query, QueryRegistry, Mutation } from 'core/index';

import { CONTENT_FIELDS } from 'apps/content/gql';

const FORKED_FROM = gql`
  fragment forkedFrom on DiffInfo {
    project {
      id
      slug
      name
      space {
        id
        spaceType
        content {
          id
          slug
        }
      }
    }
    contributionsAhead
    contributionsBehind
  }
`;

export const PROJECT_LIST_FIELDS = gql`
  ${CONTENT_FIELDS}
  fragment projectListFields on Project {
    id
    name
    canUpdate
    canDelete
    canDownload
    image {
      id
      filename
      path
      url
      completed
      private
    }
    slug
    descriptionSnippet
    private
    content {
      ...contentFields
    }
    tags {
      id
      name
    }
    parentContent {
      id
      title
      whitelabel
      avatar {
        id
        path
        url
      }
      type
      imageFallbackChar
    }
    space {
      id
      spaceType
      hasProSubscription
      trialPeriodEnd
      content {
        id
        slug
      }
    }
    contributionCount
  }
`;

const DASHBOARD_PROJECT_LIST_FIELDS = gql`
  fragment dashboardProjectListFields on Project {
    id
    name
    canUpdate
    canDelete
    canDownload
    description
    image {
      id
      filename
      path
      url
      completed
      private
    }
    slug
    descriptionSnippet
    private
    content {
      id
      pageViews
      followersCount
      whitelabel
    }
    tags {
      id
      name
    }
    space {
      id
      spaceType
      trialPeriodEnd
      content {
        id
        title
        slug
      }
    }
    contributionCount
    contributors {
      totalCount
    }
  }
`;

export const PROJECT_FIELDS = gql`
  ${FORKED_FROM}
  ${CONTENT_FIELDS}
  fragment projectFields on Project {
    id
    canUpdate
    canDelete
    canDownload
    context {
      id
    }
    forkedFrom {
      ...forkedFrom
    }
    image {
      id
      path
      filename
      url
      completed
      private
    }
    name
    slug
    description
    descriptionSnippet
    whitelabel
    license {
      name
      title
      link
      abreviation
    }
    private
    lastUpdated
    dateCreated
    content {
      ...contentFields
    }
    starCount
    isStarred
    phase {
      id
      name
      color
    }
    forkCount
    tags {
      id
      name
    }
    creatorProfile {
      id
      username
      preferredName
      avatar {
        id
        path
        url
      }
    }
    parentContent {
      id
      slug
      title
      whitelabel
      avatar {
        id
        path
        url
      }
      type
      imageFallbackChar
    }
    inSpace {
      id
      whichTypes
      spaceType
      parent {
        id
        spaceType
        content {
          id
          slug
        }
      }
      content {
        id
        slug
      }
    }
    space {
      id
      spaceType
      trialPeriodEnd
      content {
        id
        slug
      }
    }
    collections {
      id
      slug
    }
    contributions {
      totalCount
    }
    importStatus {
      status
      generalProgress
      statusProgress
      importService
    }
  }
`;

const EXTENDED_PROJECT_FIELDS = gql`
  ${PROJECT_FIELDS}
  fragment extendedProjectFields on Project {
    ...projectFields
    ... @include(if: $includeFilesInfo) {
      filesInfo {
        totalFiles
        ecadFiles
        mcadFiles
        imageFiles
        dataFiles
        documentFiles
        otherFiles
        ecadProportion
        mcadProportion
        imageProportion
        dataProportion
        documentProportion
        otherProportion
      }
    }
  }
`;

export const PROJECT_CONNECTION_FIELDS = gql`
  ${PROJECT_LIST_FIELDS}
  fragment projectConnectionFields on ProjectConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        ...projectListFields
      }
    }
  }
`;

export const HOME_PROJECT_CONNECTION_FIELDS = gql`
  ${PROJECT_LIST_FIELDS}
  fragment homeProjectConnectionFields on HomeProjectsConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        ...projectListFields
      }
    }
  }
`;

export const DASHBOARD_PROJECT_CONNECTION_FIELDS = gql`
  ${DASHBOARD_PROJECT_LIST_FIELDS}
  fragment dashboardProjectConnectionFields on ProjectConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        ...dashboardProjectListFields
      }
    }
  }
`;

export const PROJECT_MUTATION = gql`
  ${PROJECT_FIELDS}
  mutation NewProject($projectInput: ProjectInput) {
    project(projectData: $projectInput) {
      project {
        ...projectFields
      }
    }
  }
`;
export class ProjectMutation extends Mutation {
  static conf = {
    GQL: PROJECT_MUTATION,
    inputName: 'projectInput',
    resultPath: 'project.project',
  };
}

export const PROJECT_VISIBILITY_MUTATION = gql`
  mutation ProjectVisibility($projectVisibilityInput: ProjectVisibilityInput!) {
    projectVisibility(projectVisibilityInput: $projectVisibilityInput) {
      result
    }
  }
`;
export class ProjectVisibilityMutation extends Mutation {
  static conf = {
    GQL: PROJECT_VISIBILITY_MUTATION,
    inputName: 'projectVisibilityInput',
    resultPath: 'projectVisibility',
  };
}

export const FEATURED_PROJECT_MUTATION = gql`
  mutation Project($projectInput: ProjectInput) {
    project(projectData: $projectInput) {
      project {
        id
        featuredIn
      }
    }
  }
`;

export class FeaturedProjectMutation extends Mutation {
  static conf = {
    GQL: FEATURED_PROJECT_MUTATION,
    inputName: 'projectInput',
    resultPath: 'project',
  };
}

export const TRANSFER_PROJECT_MUTATION = gql`
  mutation TransferProject($tp: TransferProjectInput!) {
    transferProject(transferProjectInput: $tp) {
      project {
        id
        parentSlug
      }
    }
  }
`;

export class TransferProjectMutation extends Mutation {
  static conf = {
    GQL: TRANSFER_PROJECT_MUTATION,
    inputName: 'tp',
    resultPath: 'transferProject.project',
  };
}

export const PROJECT_QUERY = gql`
  ${EXTENDED_PROJECT_FIELDS}
  query Project(
    $space: String
    $slug: String
    $contributorsCount: Int
    $contributorsCursor: String
    $contributorsSortBy: String
    $includeFilesInfo: Boolean!
  ) {
    loggedInUser {
      id
      pendingInvites {
        id
        inviteDate
        status
        group {
          id
          space {
            id
            parent {
              id
              parent {
                id
              }
            }
          }
          name
        }
      }
    }
    project(space: $space, slug: $slug) {
      ...extendedProjectFields
      conflictsParent
      contributions {
        totalCount
      }
      contributors(
        first: $contributorsCount
        after: $contributorsCursor
        sortBy: $contributorsSortBy
      ) {
        totalCount
        pageInfo {
          startCursor
          endCursor
          hasNextPage
        }
        edges {
          cursor
          node {
            id
            fullName
            username
            imageFallbackChar
            bio
            avatar {
              id
              path
              url
              completed
            }
            content {
              ...contentFields
            }
          }
        }
      }
      isRestricted
    }
  }
`;

export class ProjectQuery extends Query {
  static conf = {
    GQL: PROJECT_QUERY,
    varNames: [
      'space',
      'slug',
      'contributorsCount',
      'contributorsCursor',
      'contributorsSortBy',
      'includeFilesInfo',
    ],
  };
}

export const CONTRIBUTORS = gql`
  ${CONTENT_FIELDS}
  query Contributors(
    $space: String
    $slug: String
    $count: Int
    $cursor: String
    $sortBy: String
  ) {
    project(space: $space, slug: $slug) {
      id
      contributors(first: $count, after: $cursor, sortBy: $sortBy) {
        totalCount
        pageInfo {
          startCursor
          endCursor
          hasNextPage
        }
        edges {
          cursor
          node {
            id
            fullName
            username
            imageFallbackChar
            bio
            avatar {
              id
              path
              url
              completed
            }
            content {
              ...contentFields
            }
          }
        }
      }
    }
  }
`;

export class ContributorsQuery extends Query {
  static conf = {
    GQL: CONTRIBUTORS,
    varNames: ['space', 'slug', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'project.contributors',
  };
}

export const COMMUNITY_PROJECTS = gql`
  ${HOME_PROJECT_CONNECTION_FIELDS}
  query HomeProjects(
    $batchSize: Int
    $cursor: String
    $sortBy: String
    $filterBy: [[String!]]
  ) {
    homeProjects(
      first: $batchSize
      after: $cursor
      sortBy: $sortBy
      filterBy: $filterBy
    ) {
      ...homeProjectConnectionFields
    }
  }
`;

export class CommunityProjectsQuery extends Query {
  static conf = {
    GQL: COMMUNITY_PROJECTS,
    varNames: ['batchSize', 'cursor', 'sortBy', 'filterBy'],
    resultPath: 'homeProjects',
  };
}

const PROFILE_PROJECTS = gql`
  ${DASHBOARD_PROJECT_CONNECTION_FIELDS}
  query ProfileProjects(
    $username: String!
    $cursor: String
    $batchSize: Int
    $sortBy: String
    $filterBy: [[String]]
    $containsBy: [[String]]
    $startsBy: [[String]]
  ) {
    profile(username: $username) {
      id
      projects(
        first: $batchSize
        after: $cursor
        sortBy: $sortBy
        startsBy: $startsBy
        filterBy: $filterBy
        containsBy: $containsBy
      ) {
        ...dashboardProjectConnectionFields
      }
    }
  }
`;
export class ProfileProjectsQuery extends Query {
  static conf = {
    GQL: PROFILE_PROJECTS,
    varNames: [
      'username',
      'cursor',
      'sortBy',
      'batchSize',
      'startsBy',
      'containsBy',
      'filterBy',
    ],
    resultPath: 'profile.projects',
  };
}

export const INITIATIVE_PROJECTS = gql`
  ${PROJECT_CONNECTION_FIELDS}
  query InitiativeProjects(
    $slug: String
    $cursor: String
    $batchSize: Int
    $sortBy: String
    $filterBy: [[String]]
    $containsBy: [[String]]
    $startsBy: [[String]]
  ) {
    initiative(slug: $slug) {
      id
      projects(
        first: $batchSize
        after: $cursor
        sortBy: $sortBy
        startsBy: $startsBy
        filterBy: $filterBy
        containsBy: $containsBy
      ) {
        ...projectConnectionFields
      }
    }
  }
`;

export class InitiativeProjectsQuery extends Query {
  static conf = {
    GQL: INITIATIVE_PROJECTS,
    varNames: [
      'slug',
      'cursor',
      'sortBy',
      'batchSize',
      'startsBy',
      'containsBy',
      'filterBy',
    ],
    resultPath: 'initiative.projects',
  };
}

export const INITIATIVE_COMMUNITY_PROFILE_PROJECTS = gql`
  ${PROJECT_CONNECTION_FIELDS}
  query InitiativeCommunityProfileProjects(
    $slug: String
    $cursor: String
    $batchSize: Int
    $sortBy: String
  ) {
    initiative(slug: $slug) {
      id
      communityProfileProjects(
        first: $batchSize
        after: $cursor
        sortBy: $sortBy
      ) {
        ...projectConnectionFields
      }
    }
  }
`;

export class InitiativeCommunityProfileProjectsQuery extends Query {
  static conf = {
    GQL: INITIATIVE_COMMUNITY_PROFILE_PROJECTS,
    varNames: ['slug', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'initiative.communityProfileProjects',
  };
}

const LICENSES = gql`
  query Licenses {
    licenses {
      name
      link
      title
      isHeader
    }
  }
`;

export class LicenseQuery extends Query {
  static conf = {
    GQL: LICENSES,
    resultPath: 'licenses',
  };
}
QueryRegistry.add('project.LicenseQuery', LicenseQuery);

const PROJECT_PHASES = gql`
  query ProjectPhases {
    projectPhases {
      id
      name
      color
      order
    }
  }
`;

export class ProjectPhaseQuery extends Query {
  static conf = {
    GQL: PROJECT_PHASES,
    resultPath: 'projectPhases',
  };
}
QueryRegistry.add('project.ProjectPhaseQuery', ProjectPhaseQuery);

const AUTCOMPLETE_PROJECT_FRAGMENT = gql`
  fragment autocompleteData on Project {
    id
    slug
    name
    space {
      id
      spaceType
      content {
        id
        slug
      }
    }
  }
`;

export const AUTOCOMPLETE_PROFILE_PROJECTS = gql`
  ${AUTCOMPLETE_PROJECT_FRAGMENT}
  query AutocompleteProfileProjects(
    $complete: String!
    $username: String!
    $first: Int
  ) {
    profile(username: $username) {
      id
      projects(first: $first, contains: ["slug", $complete]) {
        edges {
          node {
            ...autocompleteData
          }
        }
      }
    }
  }
`;

export class AutocompleteProfileProjectsQuery extends Query {
  static conf = {
    GQL: AUTOCOMPLETE_PROFILE_PROJECTS,
    varNames: ['username', 'complete', 'first'],
    resultPath: 'profile.projects.edges',
  };
}

export const AUTOCOMPLETE_INITIATIVE_PROJECTS = gql`
  ${AUTCOMPLETE_PROJECT_FRAGMENT}
  query InitiativeProjects($complete: String, $username: String, $first: Int) {
    initiative(slug: $username) {
      id
      projects(first: $first, contains: ["slug", $complete]) {
        edges {
          node {
            ...autocompleteData
          }
        }
      }
    }
  }
`;

export class AutocompleteInitiativeProjectsQuery extends Query {
  static conf = {
    GQL: AUTOCOMPLETE_INITIATIVE_PROJECTS,
    varNames: ['username', 'complete', 'first'],
    resultPath: 'initiative.projects.edges',
  };
}

const PROJECT_CHECK = gql`
  query Project($space: String, $slug: String) {
    project(space: $space, slug: $slug) {
      id
      canUpdate
      canDelete
      featuredIn
      slug
      space {
        id
        content {
          id
          slug
        }
        spaceType
      }
      private
    }
  }
`;

export class ProjectCheckQuery extends Query {
  static conf = {
    GQL: PROJECT_CHECK,
    varNames: ['space', 'slug'],
    resultPath: 'project',
  };
}

const IMPORT_PROJECT_MUTATION = gql`
  ${PROJECT_FIELDS}
  mutation ImportProject($importInput: ImportProjectInput!) {
    importProject(importInput: $importInput) {
      project {
        ...projectFields
      }
    }
  }
`;

export class ImportProjectMutation extends Mutation {
  static conf = {
    GQL: IMPORT_PROJECT_MUTATION,
    inputName: 'importInput',
    resultPath: 'importProject.project',
  };
}

const UPDATE_PROJECT_IMPORT_MUTATION = gql`
  mutation UpdateProjectImport($updateImportInput: UpdateImportProjectInput!) {
    updateImportProject(updateImportInput: $updateImportInput) {
      result
    }
  }
`;

export class UpdateProjectImportMutation extends Mutation {
  static conf = {
    GQL: UPDATE_PROJECT_IMPORT_MUTATION,
    inputName: 'updateImportInput',
    resultPath: 'updateImportProject',
  };
}

const CANCEL_PROJECT_IMPORT_MUTATION = gql`
  mutation CancelProjectImport($cancelImportInput: CancelImportProjectInput!) {
    cancelImportProject(cancelImportInput: $cancelImportInput) {
      result
    }
  }
`;

export class CancelProjectImportMutation extends Mutation {
  static conf = {
    GQL: CANCEL_PROJECT_IMPORT_MUTATION,
    inputName: 'cancelImportInput',
    resultPath: 'cancelImportProject',
  };
}

const POLL_IMPORT_STATUS = gql`
  query PollImportStatus($space: String, $slug: String) {
    project(space: $space, slug: $slug) {
      id
      importStatus {
        status
        generalProgress
        statusProgress
        importService
      }
    }
  }
`;

export class PollImportStatusQuery extends Query {
  static conf = {
    GQL: POLL_IMPORT_STATUS,
    varNames: ['space', 'slug'],
    resultPath: 'project',
  };
}

const FORK_PROJECT = gql`
  ${PROJECT_FIELDS}
  mutation Fork($forkInput: ForkProjectInput!) {
    forkProject(forkInput: $forkInput) {
      project {
        ...projectFields
      }
    }
  }
`;

export class ProjectForkMutation extends Mutation {
  static conf = {
    GQL: FORK_PROJECT,
    inputName: 'forkInput',
    resultPath: 'forkProject.project',
  };
}

export const SUGGESTED_PROJECTS = gql`
  ${PROJECT_LIST_FIELDS}
  query SuggestedProject($projectId: ID, $cursor: String, $batchSize: Int) {
    suggestedProjects(
      projectId: $projectId
      after: $cursor
      first: $batchSize
    ) {
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      totalCount
      edges {
        cursor
        node {
          ...projectListFields
        }
      }
    }
  }
`;

export class SuggestedProjectsQuery extends Query {
  static conf = {
    GQL: SUGGESTED_PROJECTS,
    varNames: ['projectId', 'cursor', 'batchSize'],
    resultPath: 'suggestedProjects',
  };
}
